import { dateTimeService, TimeService } from "@me8eon/time";
import { NameAnd } from "@me8eon/record_utils";
import { DebugLog } from "@me8eon/debug";
import { makeContextFor } from "@me8eon/context";
import { IStorage } from "@me8eon/storage";
import { decodeToken, LoginOutFn } from "@me8eon/authentication";
import { ErrorsOr } from "@me8eon/errors";


/** For derived tokens we need to call login first which returns a token (or undefined if not logged in). */
export type AccessTokenFn = (debug: DebugLog, login: LoginOutFn, storage: IStorage, timeService: TimeService) => Promise<AccessTokenDetails | undefined>

export type AccessTokenDetails = {
    token: string
    /** The expiry time of the token. This is in milliseconds since epoch. It has to be 'in the clients clock' not in server time. */
    expiry?: number
}
export type AccessTokenMetrics = {
    cacheHits: number;
    cacheMisses: number;
    successful: number;
    errors: number;
    clockErrors: number; // added metric
};

export type AccessTokenConfig = {
    fn: AccessTokenFn;
    ttl: number; // milliseconds
    timeService: TimeService;
};

export type AccessTokenData = {
    config: AccessTokenConfig
    accessToken?: Promise<string | undefined>
    expiry?: number
    rawExpiry?: number
    metrics: AccessTokenMetrics
}

export type AccessTokenStore = NameAnd<AccessTokenData>;

export const getTokenFrom = (
    tokenName: string,
    refreshLogin: LoginOutFn,
    storage: IStorage,
    access: AccessTokenData,
    debug: DebugLog,
    onClockError?: (details: { tokenName: string; expiry: number; now: number }) => void,
): Promise<string | undefined> => {
    const { fn, ttl, timeService } = access.config;
    const now = timeService().getTime();

    if (access.accessToken && access.expiry && now < access.expiry) {
        access.metrics.cacheHits += 1;
        debug("Cache hit for access token", tokenName, { expiry: access.expiry, now, "tokenDefined": !!access.accessToken });
        return access.accessToken;
    }

    access.metrics.cacheMisses += 1;
    debug("Cache miss - fetching new access token", tokenName, { now });
    access.expiry = now + ttl; //write a test for this move...
    access.rawExpiry = access.expiry;
    access.accessToken = fn(debug, refreshLogin, storage, timeService)
        .then((details) => {
            if (!details) {
                debug("Fetched undefined access token", tokenName, details);
                access.expiry = undefined;
                return undefined;
            }
            debug("Successfully fetched access token", tokenName, details);
            const { token, expiry } = details;

            if (expiry && expiry <= now) {
                access.metrics.clockErrors += 1;
                debug(`Clock error: token expiry in the past`, { tokenName, expiry, now });
                onClockError?.({ tokenName, expiry, now });
            } else access.metrics.successful += 1;

            if (expiry) access.expiry = expiry; //we keep the original expiry if the token doesn't tell us what to use
            return token;
        })
        .catch(err => {
            // Reset promise and expiry on failure to allow retries
            access.metrics.errors += 1;
            access.accessToken = undefined;
            access.expiry = undefined;
            debug.debugError(err, `Error fetching access token for ${tokenName}`);
            throw err;
        });

    return access.accessToken;
};

export const getToken = (tokens: AccessTokenStore) => async (
    tokenName: string,
    refreshLogin: LoginOutFn,
    storage: IStorage,
    debug: DebugLog,
): Promise<string | undefined> => {
    const accessData = tokens[tokenName];

    if (!accessData) {
        debug("Token configuration not found", { tokenName });
        return Promise.reject(new Error(`Token configuration not found for ${tokenName}. Legal values are ${Object.keys(tokens)}`));
    }

    return getTokenFrom(tokenName, refreshLogin, storage, accessData, debug);
};

export type AccessTokenStoreConfig = NameAnd<AccessTokenConfig>

export function createTokenStore(config: AccessTokenStoreConfig): AccessTokenStore {
    return Object.entries(config).reduce((store, [name, accessConfig]) => {
        store[name] = {
            config: accessConfig,
            metrics: { cacheHits: 0, cacheMisses: 0, errors: 0, clockErrors: 0, successful: 0 },
        };
        return store;
    }, {} as AccessTokenStore);
}

export const { use: useAccessTokenStore, Provider: AccessTokenStoreProvider } = makeContextFor<AccessTokenStore, "tokenStore">("tokenStore");


export const jwtTokenName = "jwtToken";

export const jwtTokenDetails: AccessTokenConfig = {
    ttl: 3600 * 900, // A bit less than 1 hour in milliseconds
    timeService: dateTimeService,
    fn: async (debug, login) => {
        const token = await login(debug);
        if (!token) return undefined;
        const decoded = decodeToken(token);
        const expiry = decoded.exp * 1000;
        return { token, expiry };
    },
};

export function jwtTokenFromAccessTokenStore(accessTokenStore: AccessTokenStore, refreshLogin: LoginOutFn, storage: IStorage, debug: DebugLog): () => Promise<ErrorsOr<string>> {
    return async (): Promise<ErrorsOr<string>> => {
        const accessData = accessTokenStore[jwtTokenName];

        if (!accessData)
            return { errors: [`Cannot find config for ${jwtTokenName} in ${JSON.stringify(accessTokenStore)}`] };


        const token = await getTokenFrom(jwtTokenName, refreshLogin, storage, accessData, debug, ({ tokenName, expiry, now }) => {
            debug(`Clock error for ${tokenName}: expiry ${expiry} is before now ${now}`);
        });
        if (!token) return { errors: [`No token for ${jwtTokenName}`] };
        return { value: token };
    };
}